/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import { FormHandles } from '@unform/core';
import moment from 'moment';
import { IoFilterSharp } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InputSearch, Table, Button, ConfirmModal } from '~/components';
import Pagination from '~/components/Pagination';
import { ISearch } from '~/components/Pagination/Search';
import TrainerModal from '~/components/TrainerModal';
import { useLoading } from '~/hooks/loading';
import { IPagination, PageOptionsRequest } from '~/models/Common';
import { IUser } from '~/models/User';
import { IUserListFilters } from '~/models/UserListFilters';
import { ProfessionalService } from '~/services';
import { handleYupValidationError } from '~/utils';
import { applyMask } from '~/utils/masks';
import { UserListFiltersSchema } from '~/utils/schemas/UserListFiltersSchema';

import FilterForm from './FilterForm';

import * as S from './styles';
import { ActiveOrInactive } from './styles';

const UsersList = () => {
  const { setLoading } = useLoading();
  const router = useHistory();
  const formRef = useRef<FormHandles>(null);
  const [users, setUsers] = useState<IPagination<IUser[]>>({
    data: [],
    lastPage: 0,
    page: 1,
    perPage: 1,
    total: '0',
  });
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filterText, setFilterText] = useState('Filtro avançado');
  const [filters, setFilters] = useState<IUserListFilters>({
    start_register_at: '',
    register_status: '',
    payment_status: '',
  });
  const [pageOptions, setPageOptions] = useState<PageOptionsRequest>({
    page: 1,
  });
  const [searchFilter, setSearchFilter] = useState('');

  const [trainerModalIsOpen, setTrainerModalIsOpen] = useState<boolean>(false);

  const [trainerId, setTrainerId] = useState<number>(-1);
  const [userCollaboratorId, setUserCollaboratorId] = useState<number>(-1);

  const handleNavigateToUserDetails = useCallback(
    (userId: string) => {
      router.push(`/users/details/${userId}`);
    },
    [router],
  );

  // const handleNavigateToUserCadaster = useCallback(() => {
  //   router.push(`/users/cadaster`);
  // }, [router]);

  const handleGetAllUsers = async (
    data: ISearch,
    filters: IUserListFilters,
  ) => {
    try {
      setLoading(true);

      const response = await ProfessionalService.getAllUsers(data);

      if (!response || !response.data.data) return;

      const formatResponse = response.data.data
        .filter((item) => {
          if (filters.register_status === '') return true;
          return filters.register_status === 'true'
            ? item.professional?.status
            : !item.professional?.status;
        })
        .filter((item) => {
          if (filters.payment_status === '') return true;
          if (item.professional?.contracts)
            return filters.payment_status === 'paid'
              ? item.professional?.contracts[0]?.paymentHistory[0].status ===
                  'paid'
              : item.professional?.contracts[0]?.paymentHistory[0].status !==
                  'paid';
          if (filters.payment_status !== 'paid') return true;
          return false;
        })
        .filter((item) => {
          if (filters.start_register_at === '') return true;

          if (!item.professional || !filters.start_register_at) return true;
          const registerDate = new Date(item.professional.created_at).getTime();
          const filterDate = new Date(filters.start_register_at).getTime();
          return registerDate < filterDate;
        });

      setUsers({ ...response.data, data: formatResponse });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao carregar os dados!');
    }
  };
  useEffect(() => {
    handleGetAllUsers(
      {
        searchFilter,
        page: pageOptions.page,
        perpage: 9,
      },
      filters,
    );
  }, [searchFilter, pageOptions, filters, trainerModalIsOpen]);

  const applyFilters = async (data: IUserListFilters) => {
    try {
      formRef.current?.setErrors({});
      await UserListFiltersSchema.validate(data);
      setFilterText(validateFilterButton);
      setFilters({
        ...data,
        start_register_at: data.start_register_at
          ? moment(data.start_register_at, 'DD-MM-yyyy').format('yyyy-MM-DD')
          : '',
      });
      setSearchFilter(data.search as string);
      setFilterModalVisible((prevState) => !prevState);
    } catch (error) {
      console.log(error);
      handleYupValidationError(formRef, error);
    }
  };

  const UsersTableMemo = useMemo(
    () => (
      <Table
        noItems={users.data?.length === 0}
        colsNames={[
          'Nome',
          'CPF/CNPJ',
          'E-mail',
          'Celular',
          'Controle de Acesso',
          'Status de Contrato',
          '',
        ]}
        bodyStyle={
          {
            '--td-padding-left': '20px',
          } as React.CSSProperties
        }
        headStyle={
          {
            '--th-padding-left': '20px',
          } as React.CSSProperties
        }
      >
        {users.data?.map((user) => {
          // const payment_status =
          //   user?.professional?.contracts?.[0]?.paymentHistory?.[0]?.status ??
          //   null;

          const payment_status = (() => {
            const contracts = user?.professional?.contracts;
            if (!contracts || contracts.length === 0) return null;

            const paymentHistory = contracts[0]?.paymentHistory;
            if (!Array.isArray(paymentHistory) || paymentHistory.length === 0)
              return null;

            return paymentHistory[0]?.status ?? null;
          })();

          const hasAccess =
            payment_status === null || payment_status === 'paid';

          return (
            <tr
              key={user.id}
              onClick={() => handleNavigateToUserDetails(user.id)}
            >
              <td>{user.name}</td>
              <td>
                {user?.professional?.cpf.length === 11
                  ? applyMask({
                      name: 'cpf',
                      value: user?.professional?.cpf || '',
                    })
                  : applyMask({
                      name: 'cnpj',
                      value: user?.professional?.cpf || '',
                    })}
              </td>
              <td>{user.email}</td>
              <td>
                {user.cellphone &&
                  applyMask({ name: 'telefone', value: user.cellphone })}
              </td>
              <ActiveOrInactive
                isActive={user.professional?.status}
                style={{ textAlign: 'center' }}
              >
                {user?.professional?.status ? 'Ativo' : 'Inativo'}
              </ActiveOrInactive>
              <td>
                {payment_status === 'pending'
                  ? 'Pendente'
                  : payment_status === 'paid'
                  ? 'Pago'
                  : payment_status === 'overdue'
                  ? 'Atrasado'
                  : payment_status === 'canceled'
                  ? 'Cancelado'
                  : payment_status === null
                  ? 'Teste'
                  : 'N/I'}
              </td>
              <td>{'>'}</td>
            </tr>
          );
        })}
      </Table>
    ),
    [handleNavigateToUserDetails, users],
  );

  const validateFilterButton = (): string => {
    const data = formRef.current?.getData() ?? {};
    let result = 'Filtro avançado';
    Object.values(data).forEach((value) => {
      if (value !== '') {
        result = 'Filtrando';
      }
    });
    return result;
  };

  return (
    <>
      <S.Container>
        <S.UsersListHeader>
          <S.UserListTitle>Listagem de Treinadores</S.UserListTitle>
          <InputSearch
            name="search"
            onSearch={(value) => setSearchFilter(value)}
            placeholder="Buscar por nome, e-mail ou CPF"
          />
          <Button
            onClick={() => setFilterModalVisible((prevState) => !prevState)}
            textContent={filterText}
            size="small"
            extraStyles={{ width: 180 }}
            variant={filterText !== 'Filtrando' ? 'border' : 'yellow'}
            icon={<IoFilterSharp size={20} />}
          />
          <Button
            onClick={() => {
              formRef.current?.reset({
                start_register_at: '',
                register_status: '',
                payment_status: '',
              });

              setFilters({
                payment_status: '',
                register_status: '',
                start_register_at: '',
              });

              setFilterText('Filtro avançado');

              handleGetAllUsers(
                {
                  searchFilter,
                  page: pageOptions.page,
                  perpage: 9,
                },
                {
                  payment_status: '',
                  register_status: '',
                  start_register_at: '',
                },
              );
            }}
            buttonDisable={filterText !== 'Filtrando'}
            textContent="Limpar filtros"
            size="small"
            variant="black"
          />
        </S.UsersListHeader>
        {UsersTableMemo}
        <S.UsersListFooter>
          <Button
            onClick={() => {
              setTrainerId(-1);
              setTrainerModalIsOpen(true);
            }}
            textContent="Cadastrar treinador"
            size="small"
            variant="black"
            extraStyles={{
              maxWidth: 300,
            }}
          />
          <Pagination
            pageCount={users.lastPage!}
            pageRangeDisplayed={3}
            {...{ setPageOptions }}
          />
        </S.UsersListFooter>
      </S.Container>

      <ConfirmModal
        modalIsVisible={filterModalVisible}
        setModalIsVisible={() =>
          setFilterModalVisible((prevState) => !prevState)
        }
        handleSubmit={() => formRef.current?.submitForm()}
        title="Filtro avançado"
        confirmText="Aplicar filtros"
      >
        <FilterForm
          handleSubmit={applyFilters}
          formName="professional-filters"
          formRef={formRef}
        />
      </ConfirmModal>
      <TrainerModal
        editForm={trainerId !== -1}
        trainerId={trainerId}
        userTrainerId={userCollaboratorId}
        modalIsVisible={trainerModalIsOpen}
        setModalIsVisible={setTrainerModalIsOpen}
      />
    </>
  );
};

export default UsersList;
